import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { reviews, aboutInfo } from '@/mocks/movies';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9u4f5d3pcjqs2fcslv0';

export default function AboutPage() {
  const [activeTab, setActiveTab] = useState<'intro' | 'reviews' | 'contact'>('intro');
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    const formEl = e.currentTarget;
    const formData = new FormData(formEl);

    const honeypotVal = (formData.get('phone_alt') as string || '').trim();
    if (honeypotVal) {
      setFormSuccess(true);
      formEl.reset();
      return;
    }

    const messageVal = (formData.get('message') as string || '');
    if (messageVal.length > 500) {
      setFormError('留言内容不能超过500个字符');
      return;
    }

    try {
      const resp = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'phone_alt')
            .map(([key, val]) => [key, val.toString()])
        ).toString(),
      });
      const respText = await resp.text();
      let parsed: any = {};
      try { parsed = JSON.parse(respText); } catch (_e) { /* ignore parse error */ }

      if (resp.ok && parsed?.code === 'OK') {
        setFormSuccess(true);
        formEl.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || respText || '提交失败，请稍后重试';
        setFormError(msg);
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: '关于我们' },
  ];

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <div className="py-6">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
              关于我们
            </h1>
          </div>

          <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 mb-8 w-fit">
            {([
              { key: 'intro', label: '品牌介绍' },
              { key: 'reviews', label: '用户口碑' },
              { key: 'contact', label: '联系我们' },
            ] as const).map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'bg-primary-500 text-white'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {activeTab === 'intro' && (
          <section className="py-8 md:py-12 bg-background-100">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="bg-background-50 rounded-2xl p-8 md:p-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 flex items-center justify-center bg-primary-500 rounded-xl">
                    <i className="ri-movie-2-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h2 className="text-xl font-heading font-bold text-foreground-900">
                      {aboutInfo.brandName}
                    </h2>
                    <p className="text-sm text-foreground-500">高清影视在线观看平台</p>
                  </div>
                </div>

                <p className="text-foreground-600 leading-relaxed mb-6">
                  {aboutInfo.brandIntro}
                </p>

                <div className="bg-primary-50 rounded-xl p-5 mb-8">
                  <p className="text-primary-700 font-medium text-center">
                    "{aboutInfo.mission}"
                  </p>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {aboutInfo.stats.map((stat) => (
                    <div key={stat.label} className="text-center p-4 rounded-xl bg-background-100">
                      <div className="text-2xl md:text-3xl font-heading font-bold text-primary-600">
                        {stat.value}
                      </div>
                      <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {activeTab === 'reviews' && (
          <section className="py-8 md:py-12 bg-background-100">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {reviews.map((review) => (
                  <div key={review.id} className="bg-background-50 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                        <img
                          src={review.avatar}
                          alt={review.user}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-foreground-900">{review.user}</div>
                        <div className="flex items-center gap-0.5">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <i
                              key={i}
                              className={`text-xs ${i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                            ></i>
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{review.content}</p>
                    <p className="text-xs text-foreground-400 mt-3">{review.date}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {activeTab === 'contact' && (
          <section className="py-8 md:py-12 bg-background-100">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-background-50 rounded-2xl p-6 md:p-8">
                  <h2 className="text-lg font-heading font-semibold text-foreground-900 mb-5">
                    联系方式
                  </h2>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                        <i className="ri-mail-line"></i>
                      </div>
                      <div>
                        <div className="text-xs text-foreground-500">电子邮箱</div>
                        <div className="text-sm font-medium text-foreground-900">{aboutInfo.contact.email}</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                        <i className="ri-phone-line"></i>
                      </div>
                      <div>
                        <div className="text-xs text-foreground-500">客服电话</div>
                        <div className="text-sm font-medium text-foreground-900">{aboutInfo.contact.phone}</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                        <i className="ri-wechat-line"></i>
                      </div>
                      <div>
                        <div className="text-xs text-foreground-500">微信公众号</div>
                        <div className="text-sm font-medium text-foreground-900">{aboutInfo.contact.wechat}</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                        <i className="ri-time-line"></i>
                      </div>
                      <div>
                        <div className="text-xs text-foreground-500">工作时间</div>
                        <div className="text-sm font-medium text-foreground-900">{aboutInfo.contact.workTime}</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                        <i className="ri-map-pin-line"></i>
                      </div>
                      <div>
                        <div className="text-xs text-foreground-500">公司地址</div>
                        <div className="text-sm font-medium text-foreground-900">{aboutInfo.contact.address}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-background-50 rounded-2xl p-6 md:p-8">
                  <h2 className="text-lg font-heading font-semibold text-foreground-900 mb-5">
                    在线留言
                  </h2>
                  <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
                    <div>
                      <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        您的姓名
                      </label>
                      <input
                        type="text"
                        id="about-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        电子邮箱
                      </label>
                      <input
                        type="email"
                        id="about-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容
                      </label>
                      <textarea
                        id="about-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言内容（不超过500字）"
                      ></textarea>
                    </div>

                    <div className="antispam-field">
                      <label htmlFor="about-phone-alt">备用电话</label>
                      <input
                        type="text"
                        id="about-phone-alt"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>

                    {formError && (
                      <div className="text-sm text-red-600 bg-red-50 rounded-lg px-4 py-2.5">
                        {formError}
                      </div>
                    )}
                    {formSuccess && (
                      <div className="text-sm text-green-700 bg-green-50 rounded-lg px-4 py-2.5">
                        留言提交成功！我们会尽快与您联系。
                      </div>
                    )}

                    <button
                      type="submit"
                      className="w-full px-6 py-3 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      提交留言
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
      <ScrollToTop />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "关于屁屁影院 - 高清影视在线观看平台",
            "description": aboutInfo.brandIntro,
            "url": "https://streampops.github.io/about",
            "isPartOf": {
              "@type": "WebSite",
              "name": "屁屁影院",
              "url": "https://streampops.github.io/"
            },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "首页",
                  "item": "https://streampops.github.io/"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "关于我们",
                  "item": "https://streampops.github.io/about"
                }
              ]
            }
          })
        }}
      />
    </>
  );
}